
import { reactive, inject, computed, onMounted, watch } from "vue";
import axios from "@/services/axios";
import ResearchCategorySelectionData from "@/modules/research-category-selection-data";
import ResearchGroupDto from "@/modules/research-group/research-group-dto";
import ResearchGroupRepositoryAPI from "@/modules/research-group/research-group-repository-api";
import { useI18n } from "vue-i18n";
import { location } from "@/utils/default-data";
import ResearchOrganizationRepositoryAPI from "@/modules/research-organization/research-organization-repository-api";
import {
  getPaginationValues,
  lazyParamsDefault,
} from "@/utils/pagination-helper";

export default {
  props: {
    editMode: Boolean,
  },
  setup(props: any) {
    const { t } = useI18n();
    const toast: any = inject("toast");
    const isEditModeEnabled = props.editMode as boolean;

    const state = reactive({
      users: [],
      userId: "",
      usersFiltered: [] as Array<any>,
      selectedResearchGroup: {},
      researchGroupDto: {
        users: [],
        userIds: [],
      } as ResearchGroupDto,
      lazyParams: lazyParamsDefault,
      dataLoading: false,
      promptRemoveUser: false,
      removeUserId: "",
    });
    const { getSelectedResearchGroup } = ResearchGroupRepositoryAPI();

    watch(
      () => getSelectedResearchGroup.value,
      (newValue) => {
        if (false === isEditModeEnabled) {
          return;
        }
        state.researchGroupDto = newValue;
        state.researchGroupDto.id = newValue.id;
        state.researchGroupDto.number = newValue.number;
        state.researchGroupDto.name = newValue.name;
        state.researchGroupDto.userIds = newValue.users.map(
          (item: any) => item?.id
        );

        onPaginatorUsersClick(lazyParamsDefault);
      }
    );

    const onPaginatorUsersClick = (event: any): void => {
      state.lazyParams = event;
      loadUsers();
    };

    async function loadUsers() {
      if (!state.researchGroupDto.id) {
        return;
      }
      axios
        .post(
          `api/v1/User/filter?PageNumber=${state.lazyParams.page}&PageSize=${state.lazyParams.rows}`,
          {
            researchGroupId: state.researchGroupDto.id,
          } as any
        )
        .then((response: any) => {
          state.lazyParams = getPaginationValues(response.headers);
          state.users = response.data;
          state.dataLoading = false;
        })
        .catch(() => (state.dataLoading = false));
    }

    const onUserFilterChange = (event: any) => {
      const searchValue = event.value;
      if (searchValue.length < 2) {
        return;
      }
      let apiURL = `api/v1/User/filter?PageNumber=0&PageSize=25&Keyword=${searchValue}`;
      axios
        .post(apiURL, {
          researchGroupId: null,
        } as any)
        .then((response: any) => {
          state.usersFiltered = response.data;
        });
    };
    const onUserChange = (event: any) => {
      state.userId = event.value;
    };
    const onUserAddClick = (): void => {
      let apiURL = `api/v1/ResearchGroup/${state.researchGroupDto.id}/addUser/${state.userId}`;

      axios
        .put(apiURL)
        .then((response) => {
          if (response.status === 204) {
            const summaryText = `Uporabnik dodan`;
            notificationDisplay("success", `Raziskovalna skupina`, summaryText);
            onPaginatorUsersClick(lazyParamsDefault);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna skupina`,
            `${response.statusText}: ${response.data.message}`
          );
        });
    };

    const onRemoveUserClickOpenDialog = (id: any): void => {
      state.removeUserId = id;
      state.promptRemoveUser = true;
    };

    const onRemoveUserClick = (): void => {
      let apiURL = `api/v1/ResearchGroup/${state.researchGroupDto.id}/removeUser/${state.removeUserId}`;

      axios
        .delete(apiURL)
        .then((response) => {
          if (response.status === 204) {
            const summaryText = `Uporabnik odstranjen`;
            notificationDisplay("success", `Raziskovalna skupina`, summaryText);
            onPaginatorUsersClick(lazyParamsDefault);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna skupina`,
            `${response.statusText}: ${response.data.message}`
          );
        });
      state.promptRemoveUser = false;
    };

    const onResearchGroupCreateClick = () => {
      if (isEditModeEnabled) {
        axios
          .put(
            `/api/v1/ResearchGroup/${state.researchGroupDto.id}`,
            state.researchGroupDto
          )
          .then((response) => {
            if (response.status === 204) {
              const summaryText = `${state.researchGroupDto.number} posodobljena`;
              notificationDisplay(
                "success",
                `Raziskovalna skupina`,
                summaryText
              );
            }
          })
          .catch((error) => {
            const response = error.response;
            notificationDisplay(
              "warn",
              `Raziskovalna skupina`,
              `${response.statusText}: ${response.data.message}`
            );
          });
        return;
      }
      axios
        .post("/api/v1/ResearchGroup", state.researchGroupDto)
        .then((response: any) => {
          if (response.status === 201) {
            state.researchGroupDto = {} as ResearchGroupDto;
            const summaryText = `${response.data.number} dodana`;
            notificationDisplay("success", `Raziskovalna skupina`, summaryText);
          }
        })
        .catch((error) => {
          const response = error.response;
          notificationDisplay(
            "warn",
            `Raziskovalna skupina`,
            `${response.statusText}: ${response.data.message}`
          );
        });
    };

    const notificationDisplay = (
      severity: string,
      summary: string,
      detail: string
    ): void => {
      toast.add({
        severity: severity,
        summary: summary,
        detail: detail,
        life: 3000,
      });
    };

    return {
      t,
      state: state,
      isEditModeEnabled,
      onUserFilterChange,
      onUserChange,
      onUserAddClick,
      onRemoveUserClick,
      onRemoveUserClickOpenDialog,
      onResearchGroupCreateClick,
      onPaginatorUsersClick,
    };
  },
};
